import { IconChecks, IconChevronDown, IconChevronUp, IconSearch, IconSelector } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { useMemo, useState, type ReactNode } from "react";
import {
  Badge,
  Card,
  DatePickerInput,
  DesktopOnly,
  Group,
  Input,
  Loader,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Tr,
} from "@/components/ui";
import type { BandejaAutorizacionItem } from "@/lib/types";
import { formatearMonto } from "@/shared/format/money";
import { parseIso, formatIso } from "@/features/datos-generales/utils/iso-date";
import { etiquetaTipoSolicitud, etiquetaSubtipoSolicitud } from "@/lib/solicitud-labels";
import { formatFechaHora } from "@/shared/format/fecha";
import {
  filtrarBandeja,
  ordenarBandeja,
  siguienteSort,
  type BandejaFiltros,
  type BandejaSort,
  type BandejaSortKey,
} from "../utils/ordenar-bandeja";

const COLUMNAS: {
  key: BandejaSortKey;
  label: string;
  align?: "right";
  w: string;
  /** Columnas de fecha filtran por DÍA con un selector de fecha. */
  filtro?: "fecha";
}[] = [
  { key: "folio", label: "Folio", w: "8.5%" },
  { key: "organizacion", label: "Organización", w: "8.5%" },
  { key: "area", label: "Área", w: "6.5%" },
  { key: "tipo", label: "Tipo / Subtipo", w: "7%" },
  { key: "proveedor", label: "Proveedor", w: "9.5%" },
  { key: "numProveedor", label: "Núm. Prov", w: "5.5%" },
  { key: "rfcEmisor", label: "RFC Emisor", w: "8.5%" },
  { key: "descripcion", label: "Descripción", w: "10%" },
  { key: "total", label: "Total CFDI", align: "right", w: "7%" },
  { key: "moneda", label: "Moneda", w: "5%" },
  { key: "nivel", label: "Nivel", w: "6%" },
  { key: "fechaCreacion", label: "Creación", w: "9%", filtro: "fecha" },
];

// Ancho de la columna de acciones ("Resolver") · el resto lo reparte el layout fijo.
const ANCHO_ACCIONES = "9%";

/**
 * Vista de escritorio · tabla con orden por columna (click en el encabezado) y
 * filtro de texto por columna (fila de búsqueda bajo los encabezados). Los filtros
 * viven en el padre (se envían al server); aquí además se filtra en cliente para
 * feedback instantáneo. Mobile usa `BandejaMobileList`.
 */
export function BandejaDesktopTable({
  items,
  filtros,
  buscando = false,
  onFiltrar,
}: {
  items: BandejaAutorizacionItem[];
  filtros: BandejaFiltros;
  buscando?: boolean;
  onFiltrar: (key: BandejaSortKey, valor: string) => void;
}) {
  const [sort, setSort] = useState<BandejaSort | null>(null);

  const visibles = useMemo(
    () => ordenarBandeja(filtrarBandeja(items, filtros), sort),
    [items, filtros, sort],
  );

  const alOrdenar = (key: BandejaSortKey) => setSort((prev) => siguienteSort(prev, key));

  return (
    <DesktopOnly>
      <Card padding="xs" className="relative p-0">
        {/* Indicador de búsqueda en curso · no bloquea (el usuario puede seguir escribiendo). */}
        {buscando && (
          <Group gap={6} className="pointer-events-none absolute right-3 top-2 z-10">
            <Loader size="xs" color="multiva" />
            <Text size="xs" c="dimmed" fw={500}>
              Buscando…
            </Text>
          </Group>
        )}
        <div className="overflow-x-auto">
          <Table layout="fixed" highlightOnHover verticalSpacing="sm" horizontalSpacing="xs" className="uppercase">
            <Thead>
              <Tr>
                {COLUMNAS.map((c) => (
                  <SortableTh key={c.key} sortKey={c.key} sort={sort} onSort={alOrdenar} align={c.align} w={c.w}>
                    {c.label}
                  </SortableTh>
                ))}
                <Th align="right" style={{ width: ANCHO_ACCIONES }} />
              </Tr>
              <Tr>
                {COLUMNAS.map((c) => (
                  <Th key={c.key} className="!p-1">
                    {c.filtro === "fecha" ? (
                      <DatePickerInput
                        size="xs"
                        clearable
                        placeholder="Fecha…"
                        value={parseIso(filtros[c.key])}
                        onChange={(d) => onFiltrar(c.key, formatIso(d))}
                        wrapperClassName="normal-case"
                      />
                    ) : (
                      <Input
                        size="xs"
                        placeholder="Buscar…"
                        leftSection={<IconSearch size={12} stroke={1.6} />}
                        value={filtros[c.key] ?? ""}
                        onChange={(e) => onFiltrar(c.key, e.currentTarget.value)}
                        aria-label={`Filtrar por ${c.label}`}
                        className="normal-case"
                      />
                    )}
                  </Th>
                ))}
                <Th />
              </Tr>
            </Thead>
            <Tbody className="transition-opacity duration-120" style={{ opacity: buscando ? 0.5 : 1 }}>
              {visibles.length === 0 ? (
                <Tr>
                  <Td colSpan={COLUMNAS.length + 1}>
                    <Text size="sm" c="dimmed" ta="center" className="py-3">
                      Sin resultados para los filtros.
                    </Text>
                  </Td>
                </Tr>
              ) : (
                visibles.map((it) => <BandejaDesktopRow key={it.id} it={it} />)
              )}
            </Tbody>
          </Table>
        </div>
      </Card>
    </DesktopOnly>
  );
}

/** Encabezado clickeable con indicador de orden. */
function SortableTh({
  children,
  sortKey,
  sort,
  onSort,
  align = "left",
  w,
}: {
  children: ReactNode;
  sortKey: BandejaSortKey;
  sort: BandejaSort | null;
  onSort: (key: BandejaSortKey) => void;
  align?: "left" | "right";
  w?: string;
}) {
  const activa = sort?.key === sortKey;
  const Icono = activa ? (sort!.dir === "asc" ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <Th className="!p-0 align-top" style={{ width: w }}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={`Ordenar por ${typeof children === "string" ? children : sortKey}`}
        className={`flex w-full items-start gap-0.5 px-2.5 py-2 ${align === "right" ? "justify-end" : "justify-start"}`}
      >
        <Text size="xs" fw={600} className="whitespace-normal break-words uppercase leading-tight">
          {children}
        </Text>
        <Icono size={14} stroke={1.8} className={`mt-0.5 shrink-0 ${activa ? "text-multiva-600" : "text-muted-foreground"}`} />
      </button>
    </Th>
  );
}

/** Celda de texto truncada a una línea (llena el ancho de la columna, layout fijo) + tooltip. */
function CeldaTexto({ texto, fw }: { texto: string | null | undefined; fw?: 500 }) {
  const val = texto ?? "—";
  if (!texto || texto.length <= 18) {
    return (
      <Text size="sm" truncate fw={fw}>
        {val}
      </Text>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Text size="sm" truncate fw={fw}>
          {val}
        </Text>
      </TooltipTrigger>
      <TooltipContent className="max-w-90">{val}</TooltipContent>
    </Tooltip>
  );
}

function BandejaDesktopRow({ it }: { it: BandejaAutorizacionItem }) {
  return (
    <Tr>
      <Td>
        <CeldaTexto texto={it.folio} fw={500} />
      </Td>
      <Td>
        <CeldaTexto texto={it.organizacionNombre} />
      </Td>
      <Td>
        <CeldaTexto texto={it.areaNombre} />
      </Td>
      <Td>
        <Stack gap={0}>
          <Text size="sm" truncate>{etiquetaTipoSolicitud(it.tipo)}</Text>
          <Text size="xs" c="dimmed">
            {etiquetaSubtipoSolicitud(it.subtipo)}
          </Text>
        </Stack>
      </Td>
      <Td>
        <CeldaTexto texto={it.proveedorRazonSocial} />
      </Td>
      <Td>
        <Text size="sm">{it.proveedorNumero !== null ? it.proveedorNumero : "—"}</Text>
      </Td>
      <Td>
        <CeldaTexto texto={it.rfcEmisor} />
      </Td>
      <Td>
        <CeldaTexto texto={it.descripcionPrimerConcepto} />
      </Td>
      <Td align="right">
        <Text size="sm" fw={500}>
          {it.totalCfdi !== null ? `$${formatearMonto(it.totalCfdi, null, { minimumFractionDigits: 2 })}` : "—"}
        </Text>
      </Td>
      <Td>
        <Text size="sm">{it.monedaCfdi ?? "—"}</Text>
      </Td>
      <Td>
        <Badge size="sm" color="azulProfundo">
          {it.nivelActual - 1}/{it.cantidadNiveles}
        </Badge>
      </Td>
      <Td>
        <Text size="sm">{formatFechaHora(it.fechaCreacion)}</Text>
      </Td>
      <Td align="right">
        <Link
          to={`/autorizacion/${it.id}` as never}
          className="inline-flex h-7 items-center gap-1 rounded-md bg-multiva-50 px-2.5 text-xs font-medium text-multiva-800 transition-colors hover:bg-multiva-100 dark:bg-multiva-900/40 dark:text-multiva-200"
        >
          Resolver
          <IconChecks size={12} />
        </Link>
      </Td>
    </Tr>
  );
}
